import { createContext, type ReactNode, use, useCallback, useMemo, useState } from 'react'
import { useRememberTours, useSession } from '~/lib/session'
import type { TourChapter } from './registry.ts'

/**
 * The welcome is not a chapter of steps, but it is a thing you have or have not
 * seen. It lives here rather than in the registry because whether to open it is
 * decided on every first paint — and a static import of one constant from the
 * registry would drag the whole catalog of walkthrough copy into the entry chunk,
 * which is exactly what the dynamic import below exists to avoid.
 */
export const WELCOME_ID = 'welcome'

/**
 * The walkthrough's state, and nothing else.
 *
 * It sits above the shell rather than inside a screen because a tour outlives the
 * screen it started on — half of `basics` happens on Home and the other half does
 * not. Measuring and pointing is the overlay's job; this only knows which chapter
 * is running, how far in, and what this person has already been shown.
 *
 * The registry is deliberately *not* imported for its value: it is a few kilobytes
 * of copy and route names that a signed-in customer who never opens the guide has
 * no reason to download. It arrives on the first `start()`, alongside the overlay
 * that renders it — and one static import from it would undo the whole
 * arrangement, which is why the constant above lives here.
 */

interface Run {
  chapter: TourChapter
  index: number
}

interface TourApi {
  run: Run | null
  /** True between `start()` and the chapter arriving, so a button can show it worked. */
  loading: boolean
  start: (chapterId: string, atStep?: number) => void
  go: (index: number) => void
  next: () => void
  back: () => void
  /** Ends the run. `completed` records the chapter as seen; skipping does not. */
  stop: (completed?: boolean) => void
  seen: readonly string[]
  hasSeen: (id: string) => boolean
  markSeen: (id: string) => void
  /** Un-tick one walkthrough — what "watch the welcome again" is. */
  forget: (id: string) => void
  /** "Start over" — forgets every walkthrough, welcome included. */
  forgetAll: () => void
  welcomeOpen: boolean
  closeWelcome: () => void
}

const TourContext = createContext<TourApi | null>(null)

export function TourProvider({ children }: { children: ReactNode }) {
  const user = useSession()
  const remember = useRememberTours()

  /**
   * Local first, account second. The account is the source of truth across devices,
   * but a tour that only ticked off once a round trip came back would show the
   * finished step twice on a bad connection — the same trade the language switch
   * makes, and for the same reason.
   */
  const [seen, setSeen] = useState<readonly string[]>(user.toursSeen)
  const [run, setRun] = useState<Run | null>(null)
  const [loading, setLoading] = useState(false)
  const [welcomeDismissed, setWelcomeDismissed] = useState(false)

  /**
   * Every one of these writes state *and* posts, and none of them does the posting
   * inside a `setState` updater — an updater has to be a pure function of the value
   * before it, and React reserves the right to call it more than once. They are all
   * reached from an event handler, so reading `seen` out of the closure is reading
   * what is on screen.
   */
  const persist = useCallback(
    (next: readonly string[]) => {
      setSeen(next)
      remember([...next])
    },
    [remember],
  )

  const markSeen = useCallback(
    (id: string) => {
      if (seen.includes(id)) return
      persist([...seen, id])
    },
    [seen, persist],
  )

  const start = useCallback(
    (chapterId: string, atStep = 0) => {
      setLoading(true)
      // The user is the argument, so the chapter comes back already filtered — a
      // walkthrough must never step onto a screen this account would be refused.
      import('./registry.ts')
        .then(({ chapterFor }) => {
          const chapter = chapterFor(user, chapterId)
          if (chapter) setRun({ chapter, index: Math.min(atStep, chapter.steps.length - 1) })
        })
        .catch(() => {
          // A chunk that will not load leaves the app exactly as it was, which is a
          // working app. There is nothing useful to say about it.
        })
        .finally(() => setLoading(false))
    },
    [user],
  )

  const stop = useCallback(
    (completed = false) => {
      if (completed && run) markSeen(run.chapter.id)
      setRun(null)
    },
    [run, markSeen],
  )

  const go = useCallback((index: number) => {
    setRun((current) =>
      current
        ? { ...current, index: Math.max(0, Math.min(index, current.chapter.steps.length - 1)) }
        : null,
    )
  }, [])

  /** Past the last step is not a step — it is the end, and the end is what ticks it off. */
  const next = useCallback(() => {
    if (!run) return
    if (run.index >= run.chapter.steps.length - 1) {
      markSeen(run.chapter.id)
      setRun(null)
      return
    }
    setRun({ ...run, index: run.index + 1 })
  }, [run, markSeen])

  const back = useCallback(() => {
    setRun((current) => (current ? { ...current, index: Math.max(0, current.index - 1) } : null))
  }, [])

  const forget = useCallback(
    (id: string) => {
      if (id === WELCOME_ID) setWelcomeDismissed(false)
      if (!seen.includes(id)) return
      persist(seen.filter((each) => each !== id))
    },
    [seen, persist],
  )

  const forgetAll = useCallback(() => {
    setRun(null)
    setWelcomeDismissed(false)
    persist([])
  }, [persist])

  const value = useMemo<TourApi>(
    () => ({
      run,
      loading,
      start,
      go,
      next,
      back,
      stop,
      seen,
      hasSeen: (id) => seen.includes(id),
      markSeen,
      forget,
      forgetAll,
      // Nobody has said hello to this account yet. Once is the whole point, so the
      // answer lives on the user row rather than in this tab.
      welcomeOpen: !welcomeDismissed && !seen.includes(WELCOME_ID),
      closeWelcome: () => {
        setWelcomeDismissed(true)
        markSeen(WELCOME_ID)
      },
    }),
    [
      run,
      loading,
      start,
      go,
      next,
      back,
      stop,
      seen,
      markSeen,
      forget,
      forgetAll,
      welcomeDismissed,
    ],
  )

  return <TourContext value={value}>{children}</TourContext>
}

/**
 * Falls back to a no-op rather than throwing, so a screen that offers "show me
 * around" can still be rendered on its own in a test. A button that does nothing
 * is a worse component than one that works; a component that cannot be mounted at
 * all is a worse test.
 */
const INERT: TourApi = {
  run: null,
  loading: false,
  start: () => {},
  go: () => {},
  next: () => {},
  back: () => {},
  stop: () => {},
  seen: [],
  hasSeen: () => false,
  markSeen: () => {},
  forget: () => {},
  forgetAll: () => {},
  welcomeOpen: false,
  closeWelcome: () => {},
}

export function useTour(): TourApi {
  return use(TourContext) ?? INERT
}
